import React, { useMemo, useState, useRef } from 'react';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';
import { useEffect } from 'react';
import { CustomLegend, formatKWh, formatCents, CustomStackedBarTooltip } from './ChartUtils';
import { InfoBox } from '../Components'


const PVMetricsChart = ({ data }) => {
  const [activeData, setActiveData] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const chartRef = useRef(null);
  
  const processedData = useMemo(() => {
    if (!data?.pv_metrics) return [];
    
    // Convert daily pv_metrics object to array - data.pv_metrics has date keys
    const entries = Object.entries(data.pv_metrics);
    
    return entries.map(([date, metrics]) => {
      const {
        total_sold_kwh,
        total_received_kwh,
        total_consumption_from_panels,
        total_panel_prod_for_day,
        total_income,
        total_cost_of_received
      } = metrics;
      
      return {
        date,
        // Positive values for consumption
        total_consumption_from_panels: Number(total_consumption_from_panels) || 0,
        total_received_kwh: Number(total_received_kwh) || 0,
        // Negative value for sold energy
        total_sold_kwh: -(Number(total_sold_kwh) || 0),
        // Metadata for legend
        total_panel_prod_for_day: Number(total_panel_prod_for_day) || 0,
        total_consumption: (Number(total_consumption_from_panels) || 0) + (Number(total_received_kwh) || 0),
        total_income: Number(total_income) || 0,
        total_cost_of_received: Number(total_cost_of_received) || 0
      };
    }).sort((a, b) => a.date.localeCompare(b.date)); // Sort by date
  }, [data]);

  // Define the metrics to display as bars
  const barMetrics = ['total_consumption_from_panels', 'total_received_kwh', 'total_sold_kwh'];
  
  // Color mapping for consistency with gray colors for summary metrics
  const colorMap = useMemo(() => {
    return {
      total_consumption_from_panels: '#81c784', // Light green for solar consumption
      total_received_kwh: '#64b5f6', // Light blue for grid imports
      total_sold_kwh: '#ff9800', // Orange for sold energy
      total_panel_prod_for_day: '#9e9e9e', // Gray for total production
      total_consumption: '#616161' // Darker gray for total consumption
    };
  }, []);

  const handleMouseMove = (props) => {
    if (props.activePayload && props.activePayload.length > 0) {
      setActiveData(props.activePayload[0].payload);
    }
  };

  const handleMouseLeave = () => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
  };

  useEffect(() => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
    
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };
    
    // Initial update after a short delay to ensure chart is rendered
    const timer = setTimeout(updateWidth, 100);
    
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, [processedData]);

  // Debug output
  useEffect(() => {
    console.log('PV Processed Data:', processedData);
  }, [processedData]);

  if (!processedData.length) {
    console.log('No PV metrics data available');
    return null;
  }

  // Custom tooltip for PV metrics using CustomStackedBarTooltip
  const PVTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    // Get the data from the payload
    const data = payload[0].payload;
    
    // Create custom payload with all metrics we want to show
    const customPayload = [
      {
        name: 'Aurinkosähkön kulutus',
        value: data.total_consumption_from_panels,
        color: colorMap.total_consumption_from_panels
      },
      {
        name: 'Verkkosähkö', 
        value: data.total_received_kwh,
        color: colorMap.total_received_kwh
      },
      {
        name: 'Aurinkosähkön myynti',
        value: Math.abs(data.total_sold_kwh), // Show as positive value
        color: colorMap.total_sold_kwh
      }
    ];
    
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{`Date: ${label}`}</p>
        
        {customPayload.map((entry, index) => (
          <p key={`item-${index}`} style={{ 
            margin: '3px 0', 
            color: entry.color,
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <span style={{ marginRight: '10px' }}>{entry.name}:</span>
            <span style={{ fontWeight: 'bold' }}>
              {formatKWh(entry.value)}
            </span>
          </p>
        ))}
        {/* 
        <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #eee' }} />
        
        <p style={{ 
          margin: '3px 0', 
          color: '#ff9800',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <span style={{ marginRight: '10px' }}>Income:</span>
          <span style={{ fontWeight: 'bold' }}>
            {formatCents(data.total_income)}
          </span>
        </p>
        
        <p style={{ 
          margin: '3px 0', 
          color: '#64b5f6',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <span style={{ marginRight: '10px' }}>Cost of Grid:</span>
          <span style={{ fontWeight: 'bold' }}>
            {formatCents(data.total_cost_of_received)}
          </span>
        </p>
        */}
      </div>
    );
  };

  return (
    <div ref={chartRef}>
      {/* InfoBox with cumulative data from metadata */}
      <div className="mb-4">
        <InfoBox title="Aurinkosähkön yhteenveto" dataPoints={[
          { label: "Aurinkoenergian kokonaistuotanto", value: `${formatKWh(data.metadata?.pv_metrics?.total_panel_production || 0)}` },
          { label: "Aurinkoenergian kulutus", value: `${formatKWh(data.metadata?.pv_metrics?.total_consumption_from_panels || 0)}` },
          { label: "Aurinkoenergian myynti", value: `${formatKWh(data.metadata?.pv_metrics?.total_sold_kwh || 0)}` },
          { label: "Ostettu sähkö", value: `${formatKWh(data.metadata?.pv_metrics?.total_received_kwh || 0)}` },
          { label: "Aurinkoenergian kulutus/myyntisuhde", value: `${((data.metadata?.pv_metrics?.total_consumption_from_panels || 0) / (data.metadata?.pv_metrics?.total_panel_production || 1) * 100).toFixed(1)}%` }
        ]} />
      </div>
      
      <div className="h-[400px] w-full">
        <ResponsiveContainer width="100%" height={400}>
          <BarChart 
            data={processedData}
            margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              angle={-45}
              textAnchor="end"
              height={60}
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              label={{ value: 'kWh', angle: -90, position: 'insideLeft' }}
              tickFormatter={(value) => formatKWh(value)}
            />
            
            <Tooltip content={<PVTooltip />} />
            
            {/* Reference line at zero */}
            <ReferenceLine y={0} stroke="#666" strokeWidth={1} strokeDasharray="3 3" />
            
            {/* Consumption bars (positive, stacked) */}
            <Bar
              dataKey="total_consumption_from_panels"
              stackId="consumption"
              fill={colorMap.total_consumption_from_panels}
              name="Aurinkosähkön kulutus"
            />
            <Bar
              dataKey="total_received_kwh"
              stackId="consumption"
              fill={colorMap.total_received_kwh}
              name="Verkkosähkö"
            />
            
            {/* Sold energy bars (negative) */}
            <Bar
              dataKey="total_sold_kwh"
              fill={colorMap.total_sold_kwh}
              name="Aurinkosähkön myynti"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {containerWidth > 0 && activeData && (
        <div className="mt-4">
          <CustomLegend 
            payload={[
              { dataKey: 'total_consumption_from_panels', name: 'Aurinkosähkön kulutus' },
              { dataKey: 'total_received_kwh', name: 'Verkkosähkö' },
              { dataKey: 'total_sold_kwh', name: 'Aurinkosähkön myynti' },
              { dataKey: 'total_panel_prod_for_day', name: 'Aurinkosähkän kokonaistuotanto' },
              { dataKey: 'total_consumption', name: 'Kokonaiskulutus' }
            ]}
            colors={Object.values(colorMap)}
            colorMap={{
              ...colorMap,
              total_panel_prod_for_day: '#gray-400',
              total_consumption: '#gray-600'
            }}
            activeData={activeData}
            containerWidth={containerWidth}
            processedData={processedData}
            valueFormatter={(value, dataKey) => {
              // Handle negative values for display
              if (dataKey === 'total_sold_kwh') {
                return formatKWh(Math.abs(value));
              }
              return formatKWh(value);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default PVMetricsChart;